/**
 * Two-way sync between the economic-outlook URL query string and the
 * nowcasts Zustand store.
 *
 *   - On first mount: read `country` / `timeframe` / `view` / `activity_type`
 *     / `type` from the URL and push valid values into the store.
 *   - On every store change: write the store back to the URL, omitting
 *     default values so shared links stay tidy.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNowcastsStore } from '../store';
import type {
  GdpViewSelector,
  InflationType,
  Timeframe,
  ViewMode,
} from '../types';

const TIMEFRAMES: readonly Timeframe[] = ['1y', '2y', '5y', '10y', '30y'];
const VIEWS: readonly ViewMode[] = ['gdp', 'inflation'];
const GDP_SELECTORS: readonly GdpViewSelector[] = ['Nowcast', 'Recession'];
const INFLATION_TYPES: readonly InflationType[] = ['Core', 'Headline'];

/** Return `raw` if it's one of `allowed`, else null. Validates URL params. */
function parseOneOf<T extends string>(raw: string | null, allowed: readonly T[]): T | null {
  return raw && allowed.includes(raw as T) ? (raw as T) : null;
}

/**
 * Wires the store to `useSearchParams`. Call once from the page component;
 * it owns both directions of the sync and renders nothing.
 */
export function useUrlSync(timeframe: Timeframe): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedCountry,
    view,
    gdpViewSelector,
    inflationType,
    setSelectedCountry,
    setTimeframe,
    setView,
    setGdpViewSelector,
    setInflationType,
  } = useNowcastsStore();

  // Hydrate store from URL params on first load.
  useEffect(() => {
    const countryParam = searchParams.get('country');
    const timeframeParam = parseOneOf(searchParams.get('timeframe'), TIMEFRAMES);
    const viewParam = parseOneOf(searchParams.get('view'), VIEWS);
    const gdpParam = parseOneOf(searchParams.get('activity_type'), GDP_SELECTORS);
    const typeParam = parseOneOf(searchParams.get('type'), INFLATION_TYPES);
    if (countryParam && countryParam !== selectedCountry) setSelectedCountry(countryParam);
    if (timeframeParam && timeframeParam !== timeframe) setTimeframe(timeframeParam);
    if (viewParam && viewParam !== view) setView(viewParam);
    if (gdpParam && gdpParam !== gdpViewSelector) setGdpViewSelector(gdpParam);
    if (typeParam && typeParam !== inflationType) setInflationType(typeParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push store state back to URL. Only non-default values are emitted.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('country', selectedCountry);
    next.set('timeframe', timeframe);

    if (view !== 'gdp') next.set('view', view);
    else next.delete('view');

    if (view === 'gdp') {
      next.delete('type');
      if (gdpViewSelector !== 'Nowcast') next.set('activity_type', gdpViewSelector);
      else next.delete('activity_type');
    } else {
      next.delete('activity_type');
      if (inflationType !== 'Core') next.set('type', inflationType);
      else next.delete('type');
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    selectedCountry,
    timeframe,
    view,
    gdpViewSelector,
    inflationType,
    searchParams,
    setSearchParams,
  ]);
}
